// LearnCore Frontend - 编辑奖励表单组件

import React, { useState, useEffect } from 'react';
import { rewardsApi } from '../api/tasks';
import type { RewardTemplate, RewardType } from '../types/tasks-rewards';

interface EditRewardFormProps {
  rewardId: string;
  onSuccess?: (reward: RewardTemplate) => void;
  onCancel?: () => void;
  className?: string;
}

export const EditRewardForm: React.FC<EditRewardFormProps> = ({
  rewardId,
  onSuccess,
  onCancel,
  className = ''
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'custom' as RewardType,
    dkp_cost: '',
    image_url: '',
    validity_days: '',
    max_redemptions_per_day: '1',
    is_active: true
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  useEffect(() => {
    loadReward();
  }, [rewardId]);

  const loadReward = async () => {
    try {
      setFetchLoading(true);
      const response = await rewardsApi.getReward(rewardId);

      if (response.reward) {
        const reward = response.reward;
        setFormData({
          title: reward.title || '',
          description: reward.description || '',
          type: reward.type || 'custom',
          dkp_cost: reward.dkp_cost?.toString() || '',
          image_url: reward.image_url || '',
          validity_days: reward.validity_days?.toString() || '',
          max_redemptions_per_day: reward.max_redemptions_per_day?.toString() || '1',
          is_active: reward.is_active !== false
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || '加载奖励信息失败');
    } finally {
      setFetchLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const requestData = {
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        type: formData.type,
        dkp_cost: parseInt(formData.dkp_cost),
        image_url: formData.image_url.trim() || undefined,
        validity_days: formData.validity_days ? parseInt(formData.validity_days) : undefined,
        max_redemptions_per_day: parseInt(formData.max_redemptions_per_day) || 1,
        is_active: formData.is_active
      };

      const response = await rewardsApi.updateReward(rewardId, requestData);

      if (response.reward) {
        if (onSuccess) {
          onSuccess(response.reward);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || '更新奖励失败');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(null);
  };

  const getTypeOptions = () => {
    return [
      { value: 'digital', label: '数字奖励', description: '游戏时间、应用权限等数字内容' },
      { value: 'privilege', label: '特权奖励', description: '额外的娱乐时间、特殊权限等' },
      { value: 'custom', label: '自定义奖励', description: '实物奖励、特殊体验等' }
    ];
  };

  const getRewardEmojis = () => {
    return {
      娱乐游戏: ['🎮', '🕹️', '🎯', '🎲', '🎪', '🎭', '🎨', '🎬', '📺', '📱', '💻', '🎧'],
      食物零食: ['🍕', '🍔', '🍟', '🌭', '🍿', '🍪', '🍩', '🍫', '🍬', '🧁', '🍰', '🍮', '🍨'],
      特权奖励: ['👑', '🏆', '🥇', '🎖️', '🏅', '💎', '🔑', '🎟️', '🎫', '🚪', '⏰', '📅'],
      购物奖励: ['🛍️', '🛒', '👜', '🎁', '🧸', '📚', '🖊️', '🎨', '👕', '👟', '⌚', '📱'],
      户外活动: ['🎈', '🎉', '🎊', '🎪', '🎡', '🎢', '🏖️', '🏊', '🚴', '⚽', '🏀', '🎾'],
      学习奖励: ['📚', '📖', '📝', '✏️', '📐', '📏', '🔬', '🧪', '🌟', '💡', '🏫', '🎓'],
      宠物动物: ['🐶', '🐱', '🐭', '🐹', '🦔', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁'],
      特殊体验: ['🚀', '✈️', '🚁', '🚂', '🚗', '🏍️', '🚲', '🎯', '🎪', '🌈', '⭐', '🌟']
    };
  };

  const handleEmojiSelect = (emoji: string) => {
    setFormData(prev => ({ ...prev, image_url: emoji }));
    setShowEmojiPicker(false);
  };

  if (fetchLoading) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 ${className}`}>
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
          <div className="space-y-3">
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 ${className}`}>
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* 表单标题 */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">编辑奖励</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              修改奖励的设置和内容
            </p>
          </div>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* 错误信息 */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="text-red-800 dark:text-red-400 text-sm">{error}</div>
          </div>
        )}

        {/* 奖励状态 */}
        <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <div>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => handleInputChange('is_active', e.target.checked)}
                className="mr-3 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                启用此奖励
              </span>
            </label>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 ml-7">
              关闭后孩子将无法看到和兑换此奖励
            </p>
          </div>
        </div>

        {/* 奖励标题 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            奖励标题 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            placeholder="例如：额外的游戏时间"
            maxLength={100}
            required
          />
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            最多100个字符
          </div>
        </div>

        {/* 奖励描述 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            奖励描述
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            placeholder="详细描述这个奖励的内容和获取方式..."
            rows={3}
            maxLength={500}
          />
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            最多500个字符
          </div>
        </div>

        {/* 奖励类型 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            奖励类型 <span className="text-red-500">*</span>
          </label>
          <div className="space-y-2">
            {getTypeOptions().map((option) => (
              <label
                key={option.value}
                className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
                  formData.type === option.value
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                }`}
              >
                <input
                  type="radio"
                  name="type"
                  value={option.value}
                  checked={formData.type === option.value}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  className="mt-1 mr-3"
                />
                <div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {option.label}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {option.description}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* DKP成本 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            DKP成本 <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type="number"
              value={formData.dkp_cost}
              onChange={(e) => handleInputChange('dkp_cost', e.target.value)}
              className="w-full px-3 py-2 pr-16 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="输入需要的DKP数量"
              min="1"
              max="10000"
              required
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">
              DKP
            </div>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            建议范围：10-500 DKP，根据奖励价值调整
          </div>
        </div>

        {/* 高级设置 */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">高级设置</h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 有效期 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                有效期（天）
              </label>
              <input
                type="number"
                value={formData.validity_days}
                onChange={(e) => handleInputChange('validity_days', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="不限制"
                min="1"
                max="365"
              />
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                留空表示永不过期
              </div>
            </div>

            {/* 每日兑换限制 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                每日兑换限制
              </label>
              <input
                type="number"
                value={formData.max_redemptions_per_day}
                onChange={(e) => handleInputChange('max_redemptions_per_day', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="无限制"
                min="1"
                max="10"
              />
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                每天最多可兑换次数
              </div>
            </div>
          </div>
        </div>

        {/* 奖励图片（可选） */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            奖励图片
          </label>

          <div className="space-y-3">
            {/* 当前选择的图片预览 */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center text-2xl border border-gray-300 dark:border-gray-600">
                {formData.image_url ? (
                  formData.image_url.startsWith('http') ? (
                    <img
                      src={formData.image_url}
                      alt="奖励图片"
                      className="w-full h-full object-cover rounded-lg"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <span>{formData.image_url}</span>
                  )
                ) : (
                  <span className="text-gray-400">🎁</span>
                )}
              </div>
              <div className="flex-1">
                <input
                  type="text"
                  value={formData.image_url}
                  onChange={(e) => handleInputChange('image_url', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="选择emoji或输入图片URL"
                />
              </div>
              <button
                type="button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {showEmojiPicker ? '收起' : '选择emoji'}
              </button>
            </div>

            {/* Emoji选择器 */}
            {showEmojiPicker && (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                <div className="space-y-4 max-h-64 overflow-y-auto">
                  {Object.entries(getRewardEmojis()).map(([category, emojis]) => (
                    <div key={category}>
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {category}
                      </div>
                      <div className="grid grid-cols-8 gap-2">
                        {emojis.map((emoji) => (
                          <button
                            key={emoji}
                            type="button"
                            onClick={() => handleEmojiSelect(emoji)}
                            className="w-10 h-10 bg-white dark:bg-gray-600 rounded-lg flex items-center justify-center text-xl hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors border border-gray-200 dark:border-gray-500"
                            title={emoji}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    💡 提示：选择emoji让奖励更生动有趣，或输入图片URL使用自定义图片
                  </div>
                </div>
              </div>
            )}

            <div className="text-xs text-gray-500 dark:text-gray-400">
              选择emoji或输入图片URL，让奖励更加吸引人
            </div>
          </div>
        </div>

        {/* 提交按钮 */}
        <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              取消
            </button>
          )}
          <button
            type="submit"
            disabled={loading || !formData.title.trim() || !formData.dkp_cost}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? '保存中...' : '保存更改'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditRewardForm;